import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Loader2 } from 'lucide-react';
import AccessDenied from '@/components/shared/AccessDenied';

// Loading spinner component
const LoadingScreen = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
      <p className="text-soft-silver text-sm">Loading...</p>
    </div>
  </div>
);

// Protected route wrapper - requires authentication
export const ProtectedRoute = ({ children }) => {
  const { user, loading, authInitialized } = useAuth();
  const location = useLocation();

  // Wait until auth is fully initialized
  if (!authInitialized || loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Role guard for organization routes - with permission-based route checking
export const OrgRoleGuard = ({ children }) => {
  const { user, loading, authInitialized, organization, isPlatformAdmin, needsOnboarding, session, profile } = useAuth();
  const { canAccessOrgRoutes, isDoorStaff } = usePermissions();
  const location = useLocation();

  // Wait for auth to be fully initialized
  if (!authInitialized || loading) {
    return <LoadingScreen />;
  }

  // Not authenticated
  if (!user && !session?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform admins can access everything
  if (isPlatformAdmin()) {
    return children;
  }

  // Check if user needs onboarding
  if (needsOnboarding()) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user can access org routes at all
  if (!canAccessOrgRoutes()) {
    return <Navigate to="/discover" replace />;
  }

  // Door staff special handling
  if (isDoorStaff && location.pathname !== '/app/scanner' && !location.pathname.startsWith('/app/scanner')) {
    return <Navigate to="/app/scanner" replace />;
  }

  return children;
};

// Route-level permission guard (used inside layouts)
export const RoutePermissionGuard = ({ children, routePath }) => {
  const { loading, authInitialized } = useAuth();
  const { canView } = usePermissions();
  
  if (!authInitialized || loading) {
    return <LoadingScreen />;
  }
  
  if (!canView(routePath)) {
    return (
      <AccessDenied 
        message="You don't have the required permissions to access this page. Please contact your organization administrator if you believe this is an error."
      />
    );
  }
  
  return children;
};

// Role guard for platform admin routes
export const PlatformRoleGuard = ({ children }) => {
  const { user, loading, authInitialized, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (!authInitialized || loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isPlatformAdmin()) {
    // Show access denied instead of redirect
    return (
      <div className="min-h-screen bg-obsidian">
        <AccessDenied 
          title="Platform Access Required"
          message="This area is restricted to CamPassed platform administrators."
        />
      </div>
    );
  }

  return children;
};

// Auth route guard - redirects authenticated users to dashboard
export const AuthRouteGuard = ({ children }) => {
  const { user, loading, authInitialized, needsOnboarding, profile } = useAuth();
  const { getDefaultPath } = usePermissions();

  // Show login/signup form while auth is initializing
  // This prevents a flash of loading screen on auth pages
  if (!authInitialized) {
    return children;
  }

  if (user) {
    // If user needs onboarding (has profile but no org), redirect there
    if (profile && needsOnboarding()) {
      return <Navigate to="/onboarding" replace />;
    }
    // Otherwise redirect to appropriate dashboard based on role
    return <Navigate to={getDefaultPath()} replace />;
  }

  return children;
};

// Student Route Guard - requires authenticated user
export const StudentRouteGuard = ({ children }) => {
  const { user, loading, authInitialized, session } = useAuth();
  const location = useLocation();

  // If we have a valid session, user is authenticated
  if (session?.user) {
    return children;
  }

  // Wait for auth to be fully initialized
  if (!authInitialized || loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-obsidian">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
          <div className="text-white/40 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
